import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * AlertOverlay — full-transparent page designed to be added as an OBS
 * Browser Source. Connects to /api/alerts/ws?u=...&t=... and renders alert
 * cards with configurable sounds + animations.
 *
 * URL format: /alert-overlay?u=<streamer_username>&t=<overlay_token>
 */
export default function AlertOverlay() {
  const [params] = useSearchParams();
  const u = params.get("u");
  const t = params.get("t");
  const [alert, setAlert] = useState(null);
  const [status, setStatus] = useState("connecting");
  const wsRef = useRef(null);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!u || !t) { setStatus("missing_params"); return; }
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${proto}//${window.location.host}/api/alerts/ws?u=${encodeURIComponent(u)}&t=${encodeURIComponent(t)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;
    ws.onopen = () => setStatus("connected");
    ws.onclose = () => setStatus("disconnected");
    ws.onerror = () => setStatus("error");
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "hello") return;
        showAlert(msg);
      } catch {
        /* ignore malformed frames */
      }
    };
    return () => { ws.close(); if (timerRef.current) clearTimeout(timerRef.current); };
  }, [u, t]);

  const showAlert = (msg) => {
    setAlert(msg);
    // Play sound. Custom uploads pass an absolute URL via config.custom_sound_url;
    // built-in sounds map to /alerts/<name>.mp3 in /public.
    const soundKey = msg?.config?.sound;
    if (soundKey && soundKey !== "none") {
      try {
        const src = msg?.config?.custom_sound_url || `/alerts/${soundKey}.mp3`;
        audioRef.current = new Audio(src);
        audioRef.current.volume = 0.6;
        audioRef.current.play().catch(() => {});
      } catch {
        /* audio playback blocked — silent OK */
      }
    }
    // Auto-hide.
    if (timerRef.current) clearTimeout(timerRef.current);
    const duration = msg?.config?.duration_ms || 5000;
    timerRef.current = setTimeout(() => setAlert(null), duration);
  };

  if (!u || !t) {
    return <div className="min-h-screen grid place-items-center bg-black text-red-400 text-sm">Missing ?u= or ?t= parameter</div>;
  }

  return (
    <div className="min-h-screen w-full" style={{ background: "transparent" }} data-testid="alert-overlay">
      {alert && <AlertCard alert={alert} key={alert.id} />}
      {/* Tiny debug badge — visible only when connected but no alert; useful when setting up OBS */}
      {!alert && status !== "connected" && (
        <div className="fixed top-2 left-2 text-[10px] font-mono text-white/40 bg-black/50 px-2 py-1 rounded">
          {status === "connecting" ? "Connecting…" : status === "disconnected" ? "Disconnected" : "Error"}
        </div>
      )}
    </div>
  );
}

const TYPE_STYLES = {
  follow: { accent: "#00E5FF", label: "NEW FOLLOWER" },
  sub:    { accent: "#FF3EA5", label: "NEW SUB" },
  tip:    { accent: "#D8FF00", label: "TIP" },
  raid:   { accent: "#FF7A1A", label: "INCOMING RAID" },
};

function AlertCard({ alert }) {
  const style = TYPE_STYLES[alert.type] || TYPE_STYLES.follow;
  const anim = alert?.config?.animation || "pop";
  const tpl = alert?.config?.message_template || "{username}";
  const message = tpl
    .replace("{username}", alert.from_username || "Someone")
    .replace("{amount}", alert.amount ?? "")
    .replace("{count}", alert.count ?? "");

  return (
    <div className={`fixed left-1/2 top-24 -translate-x-1/2 anim-${anim}`} data-testid="alert-overlay-card">
      <style>{`
        @keyframes popIn { 0% { transform: scale(0.6); opacity: 0 } 60% { transform: scale(1.1); opacity: 1 } 100% { transform: scale(1) } }
        @keyframes slideIn { 0% { transform: translateY(-60px); opacity: 0 } 100% { transform: translateY(0); opacity: 1 } }
        @keyframes fadeIn { 0% { opacity: 0 } 100% { opacity: 1 } }
        @keyframes shakeIt { 0%,100% { transform: translateX(0) } 25% { transform: translateX(-10px) } 75% { transform: translateX(10px) } }
        .anim-pop      { animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) }
        .anim-slide    { animation: slideIn 0.45s ease-out }
        .anim-fade     { animation: fadeIn 0.6s ease-out }
        .anim-shake    { animation: popIn 0.4s ease-out, shakeIt 0.6s 0.4s }
        .anim-confetti { animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) }
      `}</style>
      <div
        className="px-10 py-6 rounded-2xl backdrop-blur-md text-center shadow-[0_20px_60px_rgba(0,0,0,0.4)]"
        style={{
          background: "linear-gradient(180deg, rgba(20,20,28,0.95), rgba(10,10,15,0.95))",
          border: `2px solid ${style.accent}`,
          boxShadow: `0 0 40px ${style.accent}66`,
          minWidth: 480,
        }}
      >
        <div className="text-xs font-mono tracking-[0.3em] mb-2" style={{ color: style.accent }}>
          ◆ {style.label}
        </div>
        <div className="text-3xl font-black text-white tracking-tight" style={{ textShadow: `0 2px 20px ${style.accent}55` }}>
          {message}
        </div>
        {alert.test && (
          <div className="text-[10px] font-mono text-white/40 mt-3">TEST ALERT</div>
        )}
      </div>
    </div>
  );
}
